import React from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { t } from 'ttag';
import * as Yup from 'yup';
import useReactRouter from 'use-react-router';
import FormField from '../../../components/FormField';
import Loading from '../../../components/Loading';
import { USER_TYPES } from '../../../utils/enums';
import { isIntersection } from '../../../utils/helpers';
import useUser from '../../../auth/hooks';
import FormBox from '../../../components/FormBox';
import useApi from '../../../api';
import useEditForm from '../../../hooks/useEditForm';
import useCreateForm from '../../../hooks/useCreateForm';
import NewForm from '../../../components/NewForm';
import { isTrainer } from '../../../utils/permissions';
import LoadingButton from '../../../components/LoadingButton';
import { useNavigator } from '../../../components/Navigator';
import { extractDataError } from '../../../utils/errors';
import useNotifier from '../../../components/Messages/hooks';

const BaseInfo = ({ data, errors, change, user, api, edit }) => {
  const { user: authUser } = useUser();
  return (
    <>
      <Grid container>
        <Grid item sm={12}>
          <FormBox title={t`Основна інформація`}>
            {(isIntersection(user.types, [
              USER_TYPES.superManager,
              USER_TYPES.manager,
            ]) ||
              user.isAdmin) && (
              <FormField
                required
                fullWidth
                query={api.schema.trainers.many()[0]}
                preInit={edit}
                dataKey='trainers'
                variables={{}}
                type='remoteSelect'
                margin='normal'
                label={t`Виберіть тренера`}
                id='trainerId'
                name='trainerId'
                variant='outlined'
                value={data.trainerId}
                errors={errors.trainerId}
                labelKey='fullName'
                onChange={change}
              />
            )}
            <FormField
              required
              fullWidth
              query={api.schema.athletes.many()[0]}
              preInit={edit}
              dataKey='athletes'
              variables={
                isTrainer(authUser)
                  ? { inputFilter: { trainerId: data.trainerId } }
                  : { inputFilter: { trainerId: data.trainerId } }
              }
              type='remoteSelect'
              margin='normal'
              label={t`Оберіть спортсмена`}
              id='athleteId'
              name='athleteId'
              variant='outlined'
              value={data.athleteId}
              errors={errors.athleteId}
              labelKey='fullName'
              onChange={change}
            />
            <FormField
              // disabled={edit && !!data.rank}
              required
              fullWidth
              query={api.schema.ranks.ranks()[0]}
              preInit={edit}
              dataKey={api.schema.ranks.ranks()[1]}
              variables={{}}
              type={edit ? 'remoteSelect' : 'multi_select'}
              margin='normal'
              label={t`Оберіть Кю/Дан`}
              id='rank'
              name='rank'
              variant='outlined'
              value={data.rank || []}
              errors={errors.rank}
              labelKey='name'
              onChange={change}
            />
          </FormBox>
        </Grid>
      </Grid>
    </>
  );
};

const BaseForm = ({ initial, edit, onSubmit, loading }) => {
  const api = useApi();
  const { user } = useUser();
  const validationSchema = Yup.object({
    athleteId: Yup.number().required(),
  });
  return (
    <NewForm
      onSubmit={onSubmit}
      onError={() => {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      }}
      initial={initial}
      validationSchema={validationSchema}
    >
      {({ data, change, errors, submit }) => (
        <>
          <BaseInfo
            data={data}
            errors={errors}
            change={change}
            user={user}
            api={api}
            edit={edit}
          />
          <LoadingButton
            loading={loading}
            style={{ margin: '20px 0 20px 0' }}
            margin='normal'
            type='button'
            fullWidth
            variant='contained'
            color='primary'
            onClick={submit}
          >
            {t`Зберегти`}
          </LoadingButton>
        </>
      )}
    </NewForm>
  );
};

const getInitialData = (row) => ({
  id: row.id || null,
  athleteId: row.athleteId || '',
  trainerId: row.trainerId || '',
  rank: row.rank || null,
  certificationsId: row.certificationsId || null,
});

const getPostData = (row) => ({
  athleteId: row.athleteId || '',
  trainerId: row.trainerId || '',
  rank: row.rank || null,
  certificationsId: row.certificationsId || null,
});

export const EditForm = () => {
  const pushMessage = useNotifier();
  const navigate = useNavigator();
  const api = useApi();
  const { match } = useReactRouter();
  const { certificationsId } = match.params;
  const { loading, data, onSubmit, submitLoading, errors } = useEditForm({
    schema: api.schema.certifications_athlete,
    router: null,
    onComplete: (d) => {
      const res = d[api.schema.certifications_athlete.update()[1]];
      if (res.ok) {
        navigate(api.router.certifications.athleteList(certificationsId));
      } else {
        const dataError = extractDataError(res.errors);
        if (dataError) {
          pushMessage({ text: dataError });
        } else {
          pushMessage({
            text: t`Виникла помилка, перевірте чи всі поля заповнені`,
          });
        }
      }
    },
  });

  if (loading) return <Loading />;

  return (
    <Paper>
      <Box m={4}>
        <BaseForm
          loading={submitLoading}
          initial={getInitialData(data)}
          onSubmit={(formData) => onSubmit(getPostData(formData))}
          errors={errors}
          edit
        />
      </Box>
    </Paper>
  );
};

export const CreateForm = () => {
  const pushMessage = useNotifier();
  const navigate = useNavigator();
  const { match } = useReactRouter();
  const { user } = useUser();
  const certificationsId = match.params.id;
  let trainerId;
  if (
    isIntersection(user.types, [USER_TYPES.superManager, USER_TYPES.manager])
  ) {
    trainerId = undefined;
  } else if (isIntersection(user.types, [USER_TYPES.trainer])) {
    trainerId = user.id;
  }
  const api = useApi();
  const { loading, onSubmit, submitLoading, errors } = useCreateForm({
    schema: api.schema.certifications_athlete,
    router: api.router.certifications,
    onComplete: (d) => {
      const res = d[api.schema.certifications_athlete.create()[1]];
      if (res.ok) {
        navigate(api.router.certifications.athleteList(certificationsId));
      } else {
        const dataError = extractDataError(res.errors);
        if (dataError) {
          pushMessage({ text: dataError });
        } else {
          pushMessage({
            text: t`Виникла помилка, перевірте чи всі поля заповнені`,
          });
        }
      }
    },
  });

  if (loading) return <Loading />;

  return (
    <Paper>
      <Box m={4}>
        <BaseForm
          loading={submitLoading}
          initial={getInitialData({
            trainerId,
            certificationsId,
          })}
          onSubmit={(formData) => onSubmit(getPostData(formData))}
          errors={errors}
          edit={false}
        />
      </Box>
    </Paper>
  );
};
